import { api, unwrap } from '@/lib/api';

export interface NotificationTemplate {
  id: string;
  event: string;
  channel: string;
  name: string;
  subject?: string;
  content: string;
  variables: string[];
  isActive: boolean;
  isEssential: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface NotificationLogItem {
  id: string;
  recipient: string;
  channel: string;
  event?: string;
  templateId?: string;
  status: string;
  cost: number;
  errorMessage?: string;
  subject?: string;
  body?: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
}

export interface NotificationLogsResponse {
  total: number;
  limit: number;
  offset: number;
  totalCost: number;
  items: NotificationLogItem[];
}

export interface WhatsappStatusResponse {
  configured: boolean;
  connected: boolean;
  wabaId?: string;
  displayName?: string;
  number?: string;
  reason?: string;
}

export interface CustomerPreferenceResponse {
  customerId: string;
  sms: boolean;
  whatsapp: boolean;
  email: boolean;
  push: boolean;
}

export interface CustomerSearchItem {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
}

export interface CreateTemplatePayload {
  event: string;
  channel: string;
  name: string;
  subject?: string;
  content: string;
  variables?: string[];
  isActive?: boolean;
  isEssential?: boolean;
}

export interface UpdateTemplatePayload {
  name?: string;
  subject?: string;
  content?: string;
  variables?: string[];
  isActive?: boolean;
  isEssential?: boolean;
}

export const messagingApi = {
  getWhatsappStatus: (): Promise<WhatsappStatusResponse> =>
    unwrap(api.get('/admin/messaging/whatsapp/status')),

  listTemplates: (event?: string, channel?: string): Promise<NotificationTemplate[]> =>
    unwrap(
      api.get('/admin/messaging/templates', {
        params: { ...(event ? { event } : {}), ...(channel ? { channel } : {}) },
      }),
    ),

  createTemplate: (payload: CreateTemplatePayload): Promise<NotificationTemplate> =>
    unwrap(api.post('/admin/messaging/templates', payload)),

  updateTemplate: (id: string, payload: UpdateTemplatePayload): Promise<NotificationTemplate> =>
    unwrap(api.put(`/admin/messaging/templates/${id}`, payload)),

  deleteTemplate: (id: string): Promise<{ deleted: boolean }> =>
    unwrap(api.delete(`/admin/messaging/templates/${id}`)),

  previewTemplate: (
    content: string,
    subject?: string,
    variables?: Record<string, unknown>,
  ): Promise<{
    rendered: string;
    renderedSubject?: string;
    detectedVariables: string[];
    missingVariables: string[];
  }> => unwrap(api.post('/admin/messaging/templates/preview', { content, subject, variables })),

  testTemplate: (
    id: string,
    recipient: string,
    variables?: Record<string, unknown>,
  ): Promise<{ templateId: string; recipient: string; sent: boolean; channel: string }> =>
    unwrap(api.post(`/admin/messaging/templates/${id}/test`, { recipient, variables })),

  listLogs: (params?: {
    channel?: string;
    event?: string;
    status?: string;
    recipient?: string;
    limit?: number;
    offset?: number;
  }): Promise<NotificationLogsResponse> => unwrap(api.get('/admin/messaging/logs', { params })),

  searchCustomers: (q: string): Promise<{ items: CustomerSearchItem[] }> =>
    api.get('/admin/customers', { params: { q, limit: 10 } }).then((r) => {
      const raw: unknown = r.data;
      const inner =
        raw && typeof raw === 'object' && 'data' in raw
          ? ((raw as { data?: unknown }).data ?? raw)
          : raw;
      const items = Array.isArray(inner)
        ? (inner as CustomerSearchItem[])
        : inner && typeof inner === 'object' && Array.isArray((inner as { items?: unknown }).items)
          ? (inner as { items: CustomerSearchItem[] }).items
          : [];
      return { items };
    }),

  getCustomerPreferences: (customerId: string): Promise<CustomerPreferenceResponse> =>
    unwrap(api.get(`/admin/messaging/preferences/${customerId}`)),

  updateCustomerPreferences: (
    customerId: string,
    prefs: Partial<Record<'sms' | 'whatsapp' | 'email' | 'push', boolean>>,
  ): Promise<CustomerPreferenceResponse> =>
    unwrap(api.put(`/admin/messaging/preferences/${customerId}`, prefs)),
};
